import { useEffect, useMemo, useState } from 'react';
import { X, Check, ChevronDown } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { capabilityColors } from '@/lib/capability-colors';
import { CapabilityListPanel } from '@/components/capability-list/CapabilityListPanel';
import { api } from '@/lib/api-client';
import { useChat } from '../chat-provider';

interface ChatCapability {
    ref: string;
    name: string;
    description: string;
    type: string;
    methods: { name: string; modes?: string[]; channels?: string[]; tags?: string[] }[];
    status: string;
    app?: string;
    visibility?: string;
}

type VisibilityScope = 'all' | 'my' | 'global';

const VISIBILITY_SCOPE_OPTIONS: { key: VisibilityScope; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'my', label: 'My Agents' },
    { key: 'global', label: 'Global' },
];

const SORT_OPTIONS = [
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
];

const colors = capabilityColors.agent;
const Icon = colors.icon;

export function CapabilityPicker() {
    const { selectedCapability, setSelectedCapability } = useChat();
    const [capabilities, setCapabilities] = useState<ChatCapability[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState<string>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [visibilityScope, setVisibilityScope] = useState<VisibilityScope>('all');

    useEffect(() => {
        setIsLoading(true);
        Promise.all([
            api.get('/capabilities', { params: { type: 'agent', per_page: 500, include_entitled: 'true' } }),
            api.get('/capabilities', { params: { type: 'workflow', per_page: 500, include_entitled: 'true' } }),
        ])
            .then(([agentRes, workflowRes]) => {
                const agentRaw = Array.isArray(agentRes.data) ? agentRes.data : agentRes.data?.data ?? [];
                const workflowRaw = Array.isArray(workflowRes.data) ? workflowRes.data : workflowRes.data?.data ?? [];
                const raw = [...agentRaw, ...workflowRaw];
                const mapped: ChatCapability[] = raw
                    .map((item: any) => ({
                        ref: item.fqcn || `${item.app_key}/${item.name}`,
                        name: item.display_name || item.name,
                        description: item.description ?? '',
                        type: item.type ?? 'agent',
                        methods: item.methods ?? [],
                        status: item.status ?? 'active',
                        app: item.app_key,
                        visibility: item.visibility,
                    }))
                    .filter((item: ChatCapability) => {
                        const hasStreamChat = item.methods?.some(
                            (m) => m.name === 'chat' && m.channels?.includes('stream'),
                        );
                        const isAgent = item.type === 'agent';
                        return isAgent || hasStreamChat;
                    });
                setCapabilities(mapped);
                if (!selectedCapability && mapped.length > 0) {
                    setSelectedCapability(mapped[0].ref);
                }
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, []);

    const selectedName = capabilities.find((c) => c.ref === selectedCapability)?.name;

    const allTags = useMemo(() => {
        const tagSet = new Set<string>();
        for (const item of capabilities) {
            if (item.methods) {
                for (const m of item.methods) {
                    if (m.tags) {
                        for (const t of m.tags) tagSet.add(t);
                    }
                }
            }
        }
        return Array.from(tagSet).sort();
    }, [capabilities]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const filteredItems = useMemo(() => {
        let list = capabilities.filter((item) => {
            if (item.status?.toLowerCase() !== 'active') return false;

            if (visibilityScope === 'my') {
                if (item.visibility === 'global' || item.visibility === 'public') return false;
            } else if (visibilityScope === 'global') {
                if (item.visibility !== 'global' && item.visibility !== 'public') return false;
            }

            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase();
                if (
                    !item.name.toLowerCase().includes(q) &&
                    !(item.description?.toLowerCase().includes(q) ?? false) &&
                    !item.ref.toLowerCase().includes(q)
                ) {
                    return false;
                }
            }

            if (selectedTags.size > 0) {
                const itemTags = item.methods?.flatMap((m) => m.tags ?? []) ?? [];
                if (!itemTags.some((t) => selectedTags.has(t))) return false;
            }

            return true;
        });

        list = [...list].sort((a, b) => {
            let r: number;
            if (sortField === 'name') {
                r = a.name.localeCompare(b.name);
            } else {
                r = (a.description ?? '').localeCompare(b.description ?? '');
            }
            return sortDirection === 'asc' ? r : -r;
        });

        return list;
    }, [capabilities, searchQuery, selectedTags, sortField, sortDirection, visibilityScope]);

    const onClose = () => setDrawerOpen(false);

    useEffect(() => {
        if (!drawerOpen) {
            setSearchQuery('');
            setSelectedTags(new Set());
            setVisibilityScope('all');
        }
    }, [drawerOpen]);

    const handleSelect = (ref: string) => {
        setSelectedCapability(ref);
        setDrawerOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className={cn(
                    'inline-flex h-7 min-w-0 items-center gap-1 rounded-md border-none bg-transparent px-2 text-xs font-medium text-muted-foreground shadow-none transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
                )}
                aria-haspopup="dialog"
                aria-expanded={drawerOpen}
                aria-label="Select capability"
            >
                <span className="truncate">
                    {isLoading ? 'Loading...' : (selectedName ?? 'Select capability')}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-70" />
            </button>

            <SlideDrawer isOpen={drawerOpen} onClose={onClose}>
                                <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={cn(
                                                'flex h-10 w-10 items-center justify-center rounded-lg',
                                                colors.bgLight,
                                                colors.text,
                                            )}
                                        >
                                            <Icon size={20} />
                                        </div>
                                        <div>
                                            <h2 className="text-lg font-semibold text-slate-900">
                                                Select Capability
                                            </h2>
                                            <p className="text-sm text-slate-500">
                                                Choose a capability to start a conversation with
                                            </p>
                                        </div>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={onClose}
                                        className="h-8 w-8 p-0"
                                    >
                                        <X size={16} />
                                    </Button>
                                </div>

                                <CapabilityListPanel<ChatCapability>
                                    items={filteredItems}
                                    sortOptions={SORT_OPTIONS}
                                    sortField={sortField}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    searchQuery={searchQuery}
                                    onSearchChange={setSearchQuery}
                                    searchPlaceholder="Search capabilities by name or description..."
                                    allTags={allTags}
                                    selectedTags={selectedTags}
                                    extraToolbar={
                                        <div className="flex items-center rounded-md border border-slate-200 bg-white p-1">
                                            {VISIBILITY_SCOPE_OPTIONS.map((opt) => (
                                                <button
                                                    key={opt.key}
                                                    type="button"
                                                    onClick={() => setVisibilityScope(opt.key)}
                                                    className={cn(
                                                        'flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                                                        visibilityScope === opt.key
                                                            ? 'bg-slate-100 text-slate-900 shadow-sm'
                                                            : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700',
                                                    )}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    }
                                    onToggleTag={(tag) => {
                                        setSelectedTags((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(tag)) next.delete(tag);
                                            else next.add(tag);
                                            return next;
                                        });
                                    }}
                                    onClearAllTags={() => setSelectedTags(new Set())}
                                    onSelectAllTags={() => setSelectedTags(new Set(allTags))}
                                    isLoading={isLoading}
                                    loadingMessage="Loading capabilities..."
                                    emptyMessage="No capabilities found."
                                    toolbarClassName="px-12 py-4 border-b border-slate-100 shrink-0"
                                    contentClassName="flex-1 overflow-y-auto px-24 py-6"
                                    gridClassName="grid grid-cols-3 gap-3"
                                    renderCard={(item) => {
                                        const selected = item.ref === selectedCapability;
                                        return (
                                            <Card
                                                key={item.ref}
                                                className={cn(
                                                    'relative border-2 p-3 transition-all duration-200 cursor-pointer min-h-[88px]',
                                                    'border-l-4',
                                                    selected
                                                        ? 'border-green-500 bg-green-50/50 ring-2 ring-green-200'
                                                        : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-md',
                                                )}
                                                style={{
                                                    borderLeftColor: selected ? undefined : colors.hex,
                                                }}
                                                onClick={() => handleSelect(item.ref)}
                                            >
                                                <div className="flex flex-row items-start gap-3">
                                                    <div
                                                        className={cn(
                                                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                                                            colors.bgLight,
                                                            colors.text,
                                                        )}
                                                    >
                                                        <Icon size={16} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                                            {item.type}
                                                        </span>
                                                        <span className="block text-sm font-bold truncate text-slate-900">
                                                            {item.name}
                                                        </span>
                                                        {item.app && (
                                                            <span
                                                                className={cn(
                                                                    'block text-[10px] truncate',
                                                                    colors.text,
                                                                )}
                                                            >
                                                                {item.app}
                                                            </span>
                                                        )}
                                                        {item.description && (
                                                            <span className="text-[10px] text-slate-500 line-clamp-2 leading-tight block mt-0.5">
                                                                {item.description}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {selected && (
                                                        <Check
                                                            size={16}
                                                            className="shrink-0 text-green-600 mt-1"
                                                        />
                                                    )}
                                                </div>
                                            </Card>
                                        );
                                    }}
                                />
            </SlideDrawer>
        </>
    );
}
